import { routeStopPinIcon } from '../../core/route-stop-pin';
import { MapFeatureColors } from '../../core/map-object-colors';
import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import * as L from 'leaflet';
import {
  PathFeatureBase,
  PathMapObject,
} from '../../core/base/path-feature.base';
import { MapObjectId } from '../../core/map-object';
import { MapNavigationService } from '../../core/map-navigation.service';
import { MapInteractionController } from '../../components/map/map-interaction-controller';
import { doubledSegments, offsetAlongRight, RoutePoint } from './route-offset';
import {
  MapObjectPopover,
  openLayerPopover,
} from '../../core/map-object-popover';

/** The part of a planned stop this layer draws. */
export interface SalesRoutePin {
  seq: number;
  lat: number | null;
  lng: number | null;
  title: string | null;
  /** Drives the pin colour, supplied by the page so status wording stays there. */
  color: string;
  /** Identity the page addresses a stop by, so a drag says WHICH door moved
   *  rather than which drawing did. Optional: the layer draws fine without it,
   *  it just cannot report gestures. */
  key?: string;
  /** Proposed this session and not yet saved. Solid = saved, dashed amber =
   *  proposed by the user, purple = proposed by the Copilot (styles.css). */
  proposed?: boolean;
  copilot?: boolean;
}

/** One sales-route business object and the Path geometry used to draw it. */
export interface SalesRouteMapObject extends PathMapObject<MapObjectId> {
  readonly start: { lat: number; lng: number } | null;
  readonly finish: { lat: number; lng: number } | null;
  readonly pins: readonly SalesRoutePin[];
}

/** A pin being dragged, or dropped, in map coordinates. */
export interface SalesRoutePinDrag {
  readonly key: string;
  readonly lat: number;
  readonly lng: number;
}

/** One leg of the drawn route, as POST /api/sales-routes/shape returns it. */
export interface RouteLegShape {
  geoJson: string;
  /** False when the road network could not join this leg's two stops. */
  onRoad: boolean;
  distanceMeters: number;
  travelTimeSeconds: number;
}

/**
 * A rep's planned day as a layer on the persistent workspace map: the start
 * point, a numbered pin per stop, and the line joining them in order.
 *
 * The page owns the plan, the outcomes, the voice capture and the Google Maps
 * hand-off; this owns only what is drawn. Splitting it that way is what lets
 * Sales Route share the one workspace map instead of mounting a third Leaflet
 * instance behind its own card.
 */
@Injectable({ providedIn: 'root' })
export class SalesRouteFeature extends PathFeatureBase<SalesRouteMapObject> {
  static readonly Key = 'sales-route';

  override readonly key = SalesRouteFeature.Key;
  override readonly name = 'Sales Route';
  override readonly icon = 'route';
  override readonly iconColor = MapFeatureColors.salesRoutes;
  override readonly page = '/leads/sales-route';

  /**
   * Where this layer's markers sit against everything else on the shared map.
   *
   * Leaflet stacks markers by LATITUDE - anything further south draws on top -
   * which is a fine default among equals and wrong here. Places render an
   * 80x60 card of category icons and a name, anchored above their point, so a
   * place a few metres south of a stop covered that stop's number completely.
   * The number is the one thing a route map has to keep legible.
   *
   * Offsets rather than a private pane: a pane would take the whole layer out
   * of the shared marker pane, and the stops would then float over the place
   * POPUPS too - a card a person deliberately opened, hidden behind a pin they
   * did not click.
   */
  static readonly StopZIndex = 1000;
  /** Below the stops, above the lines. An arrow that covered a number would be
   *  the same defect this fixes, introduced by the fix. */
  static readonly ArrowZIndex = -1000;
  override objects: SalesRouteMapObject[] = [];

  /** A pin is being dragged: the page turns the position into "4 → 5 · +4 min"
   *  while the mouse is still down. Fires often, deliberately — it is a preview. */
  private readonly draggingSubject = new Subject<SalesRoutePinDrag>();
  readonly dragging$ = this.draggingSubject.asObservable();
  /** A pin was let go. The page decides what the new position MEANS; nothing is
   *  written and the pin snaps back to the door it belongs to. */
  private readonly droppedSubject = new Subject<SalesRoutePinDrag>();
  readonly dropped$ = this.droppedSubject.asObservable();
  /** The drag was abandoned (Escape, or a drop the page rejected). */
  private readonly cancelledSubject = new Subject<void>();
  readonly cancelled$ = this.cancelledSubject.asObservable();
  /** A pin was clicked. Selecting is the page's business; this only reports it. */
  private readonly pickedSubject = new Subject<string>();
  readonly picked$ = this.pickedSubject.asObservable();

  /**
   * The roads the day actually drives, one entry per leg, or null while none
   * has been fetched for the order now on screen.
   *
   * Null is the honest state and not an empty array: "not asked yet" and "the
   * road graph had nothing" are different, and only the second should change
   * how the line is drawn.
   */
  private legs: RouteLegShape[] | null = null;
  /** The saved Path row for this exact plan. Unlike legs, this survives reads
   *  without another road-graph search. */
  private persistedPathGeoJson: string | null = null;
  /** The path the current shape was fetched for. A shape belongs to an order
   *  of points, and this is how the feature tells "the same day, drawn again"
   *  from "a different day". */
  private shapePath: string | null = null;
  /** Frame once per activation; re-planning should not yank a rep's viewport. */
  private framed = false;
  /** Whether pins may be dragged. Off for a rep reading somebody else's day, and
   *  off before permissions have loaded: an affordance the server would refuse
   *  is worse than no affordance. */
  private draggable = false;

  constructor(
    private readonly navigation: MapNavigationService,
    private readonly zone?: NgZone,
    private readonly interaction?: MapInteractionController,
  ) {
    super();
  }

  /** A click in the itinerary uses the same camera and picked event as the map. */
  focusStop(key: string): void {
    const pin = this.objects.flatMap(route => route.pins).find(stop => stop.key === key);
    if (pin?.lat == null || pin.lng == null) return;
    this.navigation.fitBounds(L.latLngBounds([[pin.lat, pin.lng]]), { maxZoom: 17 });
    this.pickedSubject.next(key);
  }

  resetSession(): void {
    this.objects = [];
    this.legs = null;
    this.persistedPathGeoJson = null;
    this.shapePath = null;
    this.framed = false;
    this.draggable = false;
    this.clear();
    this.notifyChanged();
  }

  resetFraming(): void {
    this.framed = false;
  }

  /** Turn dragging on or off. Re-renders, because draggability is a property of
   *  the markers Leaflet has already created. */
  setDraggable(on: boolean): void {
    if (this.draggable === on) return;
    this.draggable = on;
    this.render();
  }

  get isDraggable(): boolean { return this.draggable; }

  async init(): Promise<void> {
    // Nothing at startup: the routed page supplies the plan when it opens.
  }

  /**
   * The road shape for the plan currently set, from POST /api/sales-routes/shape.
   *
   * Separate from setPlan because it arrives later: the pins are drawn at once
   * from what the page already has, and the roads replace the straight lines
   * when the graph answers. A route that never gets one keeps the straight
   * lines, which is what this map drew for its whole life before now.
   */
  setShape(legs: RouteLegShape[] | null): void {
    this.legs = legs;
    // Stamped with the path it describes, so the next setPlan can tell whether
    // it still does.
    this.shapePath = legs ? this.pathKey() : null;
    this.syncPathGeometry();
    this.render();
  }

  /** For specs: whether roads are currently held, without reading Leaflet. */
  get shapeLegs(): RouteLegShape[] | null { return this.legs; }

  /**
   * The day's saved Path row - kept only while it actually describes roads.
   *
   * A saved path is worth preferring over a fresh road-graph answer while it IS
   * the drive. One that is merely the straight line through the day's own
   * points is not: that is what the map draws when the graph cannot answer, and
   * a Save freezes it into the row. Kept, it would suppress the request that
   * would now succeed - which is how a day that drew straight lines once goes
   * on drawing them for good, on a map that has since learned the roads.
   */
  setPersistedPath(geoJson: string | null): void {
    const usable = geoJson !== null && this.describesRoads(geoJson) ? geoJson : null;
    if (this.persistedPathGeoJson === usable) return;
    this.persistedPathGeoJson = usable;
    this.syncPathGeometry();
    this.render();
  }

  /**
   * Whether a saved LineString is a ROAD shape rather than the straight line
   * between the day's own points.
   *
   * A road path begins and ends on the carriageway - at the points the graph
   * snapped the doors onto - and carries a vertex for every bend between them.
   * The straight fallback carries exactly the day's points and nothing else,
   * which is what this recognises: same count, same coordinates, same order.
   *
   * A path that cannot be read is not a road shape either. Saying so here is
   * what sends the page back to the road graph, instead of leaving it holding a
   * row it cannot draw and a request it has already decided not to make.
   */
  private describesRoads(geoJson: string): boolean {
    const line = lineStringLatLngs(geoJson);
    if (!line) return false;

    const planned = asPairs(this.drawnPoints());
    const drawn = asPairs(line);
    return drawn.length !== planned.length
      || !drawn.every((at, index) => samePoint(at, planned[index]));
  }

  /** For specs and page coordination; no Leaflet internals leak out. */
  get persistedPath(): string | null { return this.persistedPathGeoJson; }

  /**
   * The line currently drawn, ready to be stored with a manual Save - but only
   * when every metre of it is a road the graph named.
   *
   * A leg the graph could not join is the straight line between two doors, and
   * storing that turns a passing failure into the day's permanent answer: the
   * saved Path is preferred over a fresh request, so the map would keep drawing
   * the road it invented long after the graph could name the real one. Nothing
   * is stored instead, and the day goes on asking.
   */
  get pathGeoJsonForSave(): string | null {
    if (this.persistedPathGeoJson) return this.persistedPathGeoJson;
    if (!this.legs || this.shapePath !== this.pathKey()) return null;
    // One leg per pair of drawn points, and every one of them on a road.
    if (this.legs.length !== this.drawnPoints().length - 1) return null;
    if (this.legs.some(leg => !leg.onRoad)) return null;

    const joined: [number, number][] = [];
    for (const leg of this.legs) {
      const coordinates = lineStringCoordinates(leg.geoJson);
      if (!coordinates) return null;
      joined.push(...coordinates.slice(joined.length === 0 ? 0 : 1));
    }
    return joined.length < 2
      ? null
      : JSON.stringify({ type: 'LineString', coordinates: joined });
  }

  /**
   * The drive this plan describes, as a string.
   *
   * Coordinates in order and nothing else - not the colours, not the sequence
   * numbers, not the proposed flags. Those change on every recorded visit and
   * every filter, and none of them changes the road.
   */
  private pathKey(): string {
    const route = this.objects[0];
    if (!route) return '';
    const points: string[] = [];
    if (route.start) points.push(`${route.start.lat},${route.start.lng}`);
    for (const pin of route.pins) points.push(`${pin.lat},${pin.lng}`);
    if (route.finish) points.push(`${route.finish.lat},${route.finish.lng}`);
    return points.join('|');
  }

  setPlan(
    start: { lat: number; lng: number } | null,
    pins: SalesRoutePin[],
    finish: { lat: number; lng: number } | null = null,
    id: MapObjectId = 'draft',
  ): void {
    // Drawn only when it is somewhere else: a day that ends where it began would
    // otherwise stack a finish marker on top of the start one and look like a bug.
    const visibleFinish = finish && start && samePlace(finish, start) ? null : finish;
    this.objects = [{
      id,
      start,
      finish: visibleFinish,
      pins: pins.filter(p => p.lat != null && p.lng != null),
      pathGeoJson: null,
    }];

    // The old shape is dropped only if it no longer describes this path.
    //
    // It used to be dropped unconditionally, which was correct about a
    // reordered day and wrong about everything else: the page re-renders on
    // every recorded visit, every filter and the edit session's own state
    // stream, and it does NOT re-fetch a shape for a path it has already
    // asked about. So the roads arrived, the next render wiped them, and
    // nothing ever asked again - the map went back to straight lines and
    // stayed there. That is the "it still does not work" this feature was
    // reported for twice.
    if (this.shapePath !== null && this.shapePath !== this.pathKey()) {
      this.legs = null;
      this.shapePath = null;
    }
    this.syncPathGeometry();
    this.render();
  }

  /** Keep the Path contract on the object in sync with the line being drawn. */
  private syncPathGeometry(): void {
    const route = this.objects[0];
    if (!route) return;
    this.objects = [{ ...route, pathGeoJson: this.pathGeoJsonForSave }];
  }

  /**
   * The points the drawn line runs through, in order: the start, each stop, and
   * the finish - or back to the start when the day returns to where it began.
   *
   * The same list the page asks the road graph about, which is what makes a leg
   * count comparable to it, and what lets a saved Path be told apart from the
   * straight line through those very same points.
   */
  private drawnPoints(): L.LatLngExpression[] {
    const route = this.objects[0];
    if (!route) return [];
    const points: L.LatLngExpression[] = [];
    if (route.start) points.push([route.start.lat, route.start.lng]);
    for (const pin of route.pins) points.push([pin.lat!, pin.lng!]);
    if (route.finish && route.pins.length) points.push([route.finish.lat, route.finish.lng]);
    else if (route.start && route.pins.length) points.push([route.start.lat, route.start.lng]);
    return points;
  }

  override render(): void {
    this.layer.clearLayers();
    if (!this.enabled) return;

    const route = this.objects[0];
    if (!route) return;

    const path = this.drawnPoints();

    if (route.start) {
      const at: L.LatLngExpression = [route.start.lat, route.start.lng];
      const marker = L.circleMarker(at, {
        radius: 7, color: '#37474f', fillColor: '#ffffff', fillOpacity: 1, weight: 2,
      }).bindTooltip('Start');
      this.bindLazyPopover(marker, () => this.routePopover(route, 'Start'));
      marker.addTo(this.layer);
    }

    for (const pin of route.pins) {
      const at: L.LatLngExpression = [pin.lat!, pin.lng!];
      // Numbered so the map and the list read as the same plan. Styled inline
      // rather than by class: Leaflet appends the icon outside any component,
      // where emulated-encapsulation styles do not reach. The proposed ring is
      // the same amber / purple convention styles.css gives the list.
      // The proposed / Copilot rings come from styles.css, the same two classes
      // the list rows use - one convention, defined once, reaching a Leaflet
      // icon that lives outside every component's encapsulation.
      // A PIN, not a dot, and that is the point of the shape.
      //
      // Route stops keep their own numbered pin representation while Customer
      // and Lead features render their own Place-backed markers independently.
      const marker = L.marker(at, {
        draggable: this.draggable && !!pin.key,
        autoPan: true,
        zIndexOffset: SalesRouteFeature.StopZIndex,
        // Above the places and the leads sharing this map: while a route is on
        // screen, its numbered stops are the subject and everything else is
        // context.
        icon: routeStopPinIcon(pin.seq, pin.color, pin.copilot ? 'es-copilot' : pin.proposed ? 'es-proposed' : ''),
      });
      marker.bindTooltip(`${pin.seq}. ${pin.title ?? 'Stop'}`).addTo(this.layer);
      this.bindLazyPopover(marker, () => this.stopPopover(pin));

      const key = pin.key;
      if (!key) continue;
      marker.on('click', () => this.insideAngular(
        () => this.pickedSubject.next(key)));
      if (!this.draggable) continue;
      marker.on('drag', e => {
        const p = (e.target as L.Marker).getLatLng();
        this.insideAngular(() =>
          this.draggingSubject.next({ key, lat: p.lat, lng: p.lng }));
      });
      marker.on('dragend', e => {
        const p = (e.target as L.Marker).getLatLng();
        // The door did not move - only its place in the day. Snapping back
        // before the page reacts keeps the map from ever showing a pin at a
        // coordinate no stop has.
        marker.setLatLng(at);
        this.insideAngular(() =>
          this.droppedSubject.next({ key, lat: p.lat, lng: p.lng }));
      });
    }

    // The drive home is planned and already counted in the day's total, so it is
    // drawn: a total that includes a leg the map does not show reads as an error.
    // A day that finishes where it started gets no second marker - only the leg
    // back, which drawnPoints() has already put into the line.
    if (route.finish && route.pins.length) {
      const at: L.LatLngExpression = [route.finish.lat, route.finish.lng];
      const marker = L.circleMarker(at, {
        radius: 7, color: '#37474f', fillColor: '#37474f', fillOpacity: 1, weight: 2,
      }).bindTooltip('Finish');
      this.bindLazyPopover(marker, () => this.routePopover(route, 'Finish'));
      marker.addTo(this.layer);
    }

    if (path.length > 1) {
      // Solid = the order that is saved. Dashed amber = proposed this session;
      // purple = proposed by the Copilot. Same convention as the list, so the
      // two halves of the page cannot claim different things about one day.
      const copilot = route.pins.some(p => p.copilot);
      const proposed = copilot || route.pins.some(p => p.proposed);
      const style: L.PolylineOptions = {
        // `.leaflet-interactive.es-proposed` / `.es-copilot` in styles.css set
        // the stroke and the dashes; the colour here is what a browser without
        // those rules would still draw.
        className: copilot ? 'es-copilot' : proposed ? 'es-proposed' : '',
        color: copilot ? '#5e35b1' : proposed ? '#e65100' : MapFeatureColors.salesRoutes,
        weight: 3,
        opacity: proposed ? 0.9 : 0.7,
        dashArray: proposed ? '6 5' : undefined,
      };

      // What a stretch with no road behind it is drawn as: thinner, fainter and
      // dotted, because a straight line presented as a road is a lie about the
      // drive. For a leg the graph could not join AND for a whole day it could
      // not answer for - the second used to be drawn as though it WERE the
      // road, which is the same lie told over the entire day at once.
      const offRoad: L.PolylineOptions = {
        ...style, weight: 2, opacity: 0.55, dashArray: '2 6',
      };

      // The roads, when they are known and describe THIS path. A leg count
      // that does not match means the shape belongs to a different order -
      // one arriving late after a re-order - and drawing it would show a day
      // nobody planned. The straight line is the honest fallback.
      const persistedPath = this.persistedPathGeoJson
        ? lineStringLatLngs(this.persistedPathGeoJson)
        : null;
      const legs = this.legs?.length === path.length - 1 ? this.legs : null;

      const arrowColor = style.color!;
      if (persistedPath) {
        // A persisted Path is one continuous LineString rather than separate
        // preview legs. It can still contain an out-and-back, so apply the
        // same bidirectional offset over all of its segments before drawing.
        const saved = asPairs(persistedPath);
        const drawn = offsetAlongRight(saved, doubledSegments([saved]));
        const line = L.polyline(drawn, style);
        this.bindLazyPopover(line, () => this.routePopover(route));
        line.addTo(this.layer);
        this.drawArrows(drawn, arrowColor);
      } else if (legs) {
        const shapes = legs.map((leg, index) => asPairs(
          lineStringLatLngs(leg.geoJson)
            // A leg the graph could not join keeps the straight line between
            // its own two stops - drawn thinner and dashed, because a straight
            // line presented as a road is a lie about the drive.
            ?? [path[index], path[index + 1]] as L.LatLngExpression[]));
        // Which stretches the day drives BOTH ways, decided across every leg
        // at once. A leg on its own cannot know it is retracing one that came
        // before it, and retracing is the whole question.
        const doubled = doubledSegments(shapes);
        shapes.forEach((shape, index) => {
          const drawn = offsetAlongRight(shape, doubled);
          const line = L.polyline(drawn, legs[index].onRoad ? style : offRoad);
          this.bindLazyPopover(
            line,
            () => this.routePopover(route, `Leg ${index + 1}`),
          );
          line.addTo(this.layer);
          // Per leg, not over the whole path: arrows are how the map says
          // which way a leg is driven, and a leg is exactly the unit that has
          // a direction. Spacing them over the joined path would put none at
          // all on a short leg between two long ones. Along the DRAWN line, so
          // an arrow sits on the carriageway it belongs to rather than in the
          // gap between the two.
          this.drawArrows(drawn, arrowColor);
        });
      } else {
        // No road geometry for this day at all - the graph was never asked, the
        // answer never came, or it did not describe this order. The order is
        // still worth drawing, but as what it is: the line BETWEEN the doors,
        // not the drive between them. Same offset treatment, because a day that
        // goes A to B and back drives one straight line twice.
        const straight = asPairs(path);
        const drawn = offsetAlongRight(straight, doubledSegments([straight]));
        const line = L.polyline(drawn, offRoad);
        this.bindLazyPopover(line, () => this.routePopover(route));
        line.addTo(this.layer);
        this.drawArrows(drawn, arrowColor);
      }

      if (!this.framed) {
        this.framed = true;
        this.navigation.fitBounds(
          L.latLngBounds(path as L.LatLngTuple[]).pad(0.15),
          { maxZoom: 15 },
        );
      }
    }
  }

  private routePopover(
    route: SalesRouteMapObject,
    title = 'Sales Route',
  ): MapObjectPopover {
    const distanceMeters = this.legs?.reduce(
      (total, leg) => total + leg.distanceMeters,
      0,
    );
    const travelTimeSeconds = this.legs?.reduce(
      (total, leg) => total + leg.travelTimeSeconds,
      0,
    );
    return {
      type: 'Path',
      title,
      subtitle: route.id === 'draft' ? 'Draft route' : `Route ${route.id}`,
      values: [
        { label: 'Stops', value: route.pins.length },
        {
          label: 'Distance',
          value: distanceMeters === undefined
            ? undefined
            : `${(distanceMeters / 1000).toFixed(1)} km`,
        },
        {
          label: 'Travel time',
          value: travelTimeSeconds === undefined
            ? undefined
            : formatDuration(travelTimeSeconds),
        },
        {
          label: 'Geometry',
          value: this.persistedPathGeoJson
            ? 'Saved path'
            : this.legs
              ? 'Calculated path'
              : 'Straight-line preview',
        },
      ],
    };
  }

  private bindLazyPopover(
    layer: L.Layer,
    model: () => MapObjectPopover,
  ): void {
    layer.on('click', () => {
      if (this.interaction && !this.interaction.canInspect) {
        layer.closePopup();
        layer.unbindPopup();
        return;
      }
      openLayerPopover(layer, model());
    });
  }

  private insideAngular<T>(action: () => T): T {
    return this.zone ? this.zone.run(action) : action();
  }

  private stopPopover(pin: SalesRoutePin): MapObjectPopover {
    return {
      type: 'Sales route stop',
      title: pin.title?.trim() || `Stop ${pin.seq}`,
      values: [
        { label: 'Sequence', value: pin.seq },
        {
          label: 'State',
          value: pin.copilot
            ? 'Copilot proposal'
            : pin.proposed
              ? 'Proposed'
              : 'Saved',
        },
      ],
    };
  }

  /**
   * Arrowheads along one drawn leg.
   *
   * Non-interactive: they sit ON the line a person clicks and drags stops
   * near, and a decoration that swallows those events is worse than no
   * decoration. `interactive: false` also keeps them out of the layer's
   * hit-testing entirely, which matters when a long day draws a few hundred.
   *
   * The glyph points north at rotation 0, so the CSS rotation IS the bearing -
   * no offset to get wrong, and the spec can assert the number it computed.
   *
   * A CHEVRON, stroked, rather than the filled arrowhead this used to draw. An
   * arrowhead is a symbol sitting on the route; a chevron of the same weight
   * as the line reads as part of it, which is what keeps a route with a
   * hundred of them looking like a route rather than a row of icons. The white
   * casing under it is the same trick the line itself uses to stay legible
   * over a busy basemap.
   */
  private drawArrows(points: RoutePoint[], color: string): void {
    for (const arrow of arrowsAlong(points)) {
      L.marker([arrow.lat, arrow.lng], {
        interactive: false,
        keyboard: false,
        zIndexOffset: SalesRouteFeature.ArrowZIndex,
        icon: L.divIcon({
          className: 'sr-arrow',
          html:
            `<svg width="14" height="14" viewBox="0 0 14 14" `
            + `style="transform:rotate(${arrow.bearing.toFixed(1)}deg)">`
            + `<path d="M3 9.5 L7 4 L11 9.5" fill="none" stroke="#fff" `
            + `stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`
            + `<path d="M3 9.5 L7 4 L11 9.5" fill="none" stroke="${color}" `
            + `stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`
            + `</svg>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
      }).addTo(this.layer);
    }
  }

  override clear(): void {
    this.layer.clearLayers();
  }
}

/**
 * Reads a GeoJSON LineString into Leaflet's order.
 *
 * GeoJSON is [longitude, latitude] and Leaflet is [latitude, longitude].
 * Getting that backwards does not throw - it draws the day in the wrong
 * hemisphere - so the swap happens in exactly this one place.
 *
 * Null for anything that is not a line with points in it, so a malformed
 * payload falls back to the straight leg rather than throwing inside a
 * render and taking the whole map down with it.
 */
/** Leaflet accepts several shapes for a point; the geometry here wants one. */
function asPairs(points: L.LatLngExpression[]): RoutePoint[] {
  return points.map(point => Array.isArray(point)
    ? [point[0], point[1]] as RoutePoint
    : [(point as L.LatLngLiteral).lat, (point as L.LatLngLiteral).lng]);
}

/** One arrowhead on the drawn route: where it sits and which way it points. */
export interface RouteArrow {
  lat: number;
  lng: number;
  /** Compass bearing in degrees, 0 = north, increasing clockwise - the same
   *  convention the routing engine's turn costs use. */
  bearing: number;
}

/**
 * Earth radius in metres, for arrow spacing. Spacing is a rough visual
 * quantity, so a sphere is plenty.
 */
const EarthRadiusMeters = 6_371_000;

/** Metres between two points, well enough to space arrows by. */
function metersBetween(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * toRad;
  const dLng = (b[1] - a[1]) * toRad;
  const midLat = ((a[0] + b[0]) / 2) * toRad;
  const x = dLng * Math.cos(midLat);
  return Math.hypot(dLat, x) * EarthRadiusMeters;
}

/**
 * Which way you are travelling along a segment, as a compass bearing.
 *
 * Longitude is scaled by cos(latitude) because a degree of longitude is
 * shorter than a degree of latitude everywhere but the equator - without it
 * every arrow in Chicago would lean about 40 degrees off the road it sits on.
 */
export function bearingBetween(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180;
  const north = b[0] - a[0];
  const east = (b[1] - a[1]) * Math.cos(((a[0] + b[0]) / 2) * toRad);
  const degrees = Math.atan2(east, north) / toRad;
  return (degrees + 360) % 360;
}

/**
 * Arrowheads along a drawn path, so a route says which way it is driven.
 *
 * Spaced by DISTANCE along the path rather than by vertex: a road's geometry
 * has hundreds of points through a bend and two down a straight, so one arrow
 * per vertex would clump in the corners and vanish on the motorway - the
 * opposite of where a direction is hard to read.
 *
 * Every path gets at least one arrow (at its midpoint) as long as it has any
 * length at all: a short leg is still a leg with a direction, and a leg drawn
 * without one reads as the map having forgotten it.
 *
 * @param everyMeters roughly how far apart arrows should sit.
 * @param max a ceiling per path, so a cross-country leg does not become a
 *            queue of hundreds of markers the browser has to lay out.
 */
export function arrowsAlong(
  points: [number, number][],
  everyMeters = 400,
  max = 24,
): RouteArrow[] {
  if (points.length < 2 || everyMeters <= 0 || max <= 0) return [];

  // Segment lengths first: the total decides the spacing actually used, so a
  // short path is not left without an arrow and a long one is not flooded.
  const lengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const length = metersBetween(points[i], points[i + 1]);
    lengths.push(length);
    total += length;
  }
  if (total <= 0) return [];

  const wanted = Math.min(max, Math.max(1, Math.round(total / everyMeters)));
  // Offset by half a step so arrows sit BETWEEN the ends rather than on top of
  // the stop markers, which is where they would be unreadable.
  const step = total / wanted;

  const arrows: RouteArrow[] = [];
  let target = step / 2;
  let travelled = 0;
  for (let i = 0; i < lengths.length && arrows.length < wanted; i++) {
    const length = lengths[i];
    while (arrows.length < wanted && target <= travelled + length) {
      const into = length > 0 ? (target - travelled) / length : 0;
      const from = points[i];
      const to = points[i + 1];
      arrows.push({
        lat: from[0] + (to[0] - from[0]) * into,
        lng: from[1] + (to[1] - from[1]) * into,
        bearing: bearingBetween(from, to),
      });
      target += step;
    }
    travelled += length;
  }
  return arrows;
}

export function lineStringLatLngs(geoJson: string): L.LatLngExpression[] | null {
  const points = lineStringCoordinates(geoJson);
  return points?.map(([lng, lat]) => [lat, lng] as L.LatLngExpression) ?? null;
}

function lineStringCoordinates(geoJson: string): [number, number][] | null {
  try {
    const parsed = JSON.parse(geoJson) as {
      type?: string;
      coordinates?: [number, number][];
    };
    const points = parsed?.coordinates;
    if (parsed?.type !== 'LineString'
        || !Array.isArray(points)
        || points.length < 2
        || points.some(point => !Array.isArray(point)
          || point.length < 2
          || !Number.isFinite(point[0])
          || !Number.isFinite(point[1]))) return null;
    return points;
  } catch {
    return null;
  }
}

/** Same to about a centimetre: fine enough that no point the graph snapped onto
 *  a road can be mistaken for the door it snapped from, coarse enough to survive
 *  a round trip through GeoJSON. */
function samePoint(a: RoutePoint, b: RoutePoint): boolean {
  return Math.abs(a[0] - b[0]) < 1e-7 && Math.abs(a[1] - b[1]) < 1e-7;
}

/** Same to about a metre — enough to tell "back to the depot" from "somewhere else". */
function samePlace(a: { lat: number; lng: number }, b: { lat: number; lng: number }): boolean {
  return Math.abs(a.lat - b.lat) < 1e-5 && Math.abs(a.lng - b.lng) < 1e-5;
}

function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
}
